import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCountdown } from '@make-software/tg-mini-app-ui';

import { TxStatus, GetVoteTransactionResponseType } from '@/entities';
import {
  selectTxIsOpen,
  selectTxStatus,
  openTxModal,
  closeTxModal,
  setTxStatus,
  useSeasonInfo,
  useMe,
  telegramClientApi,
  AppDispatch,
} from '@/shared';
import { useAuthData } from '@/features';
import {
  buildRedirectLinkToCWM,
  getDevicePlatform,
  getEncodedAuthData,
  openLink,
  shouldBeRedirectedToCWM
} from '@/utils';

interface VoteTxData extends GetVoteTransactionResponseType {
  error: string | null;
};


const emptyVoteTxData: VoteTxData = {
  error: null,
  transaction: null,
  vote_id: null,
  project_address: null,
  token_amount: null,
  sender_pk: null,
  sender_acc_hash: null,
  expiration: null,
  voting_capacity: null,
  voting_capacity_resets_at: null
};

interface UseVoteFormArgs {
  projectAddress?: string;
  onProcessed?: () => void;
}

const parseAmount = (val: string | number): number => {
  const n =
    typeof val === 'string' ? Number(val.replace(/,/g, '.').replace(/[^\d.]/g, '')) : Number(val);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

export const useVoteForm = ({ projectAddress, onProcessed }: UseVoteFormArgs) => {
  const { initData } = useAuthData();

  const dispatch = useDispatch<AppDispatch>();
  const isTxModalOpen = useSelector(selectTxIsOpen);
  const txStatus = useSelector(selectTxStatus);

  const { data: airdropInfo } = useSeasonInfo();
  const meService = useMe();
  const me = meService.data;

  const senderPK = me?.wallet?.public_key ?? null;
  const fansBalance = meService?.data?.balance[airdropInfo?.airdrop?.round ?? 1]?.balance ?? 0;

  const [amount, setAmount] = React.useState<number>(0);
  const [submitting, setSubmitting] = React.useState(false);

  const walletBalance = Number((window as any).csprclick?.getActiveAccount()?.balance) || 0;
  const txFee = airdropInfo?.airdrop?.vote_tx_fee ?? 6000000000;

  const voteAmounts = [200, 500, 1000];

  const [voteTxData, setVoteTxData] = React.useState<VoteTxData | null>(null);
  const votingCapacity: number | null = voteTxData?.voting_capacity ?? null;

  const isVotingCapacityKnown = voteTxData?.voting_capacity != null;

  const remainingVotingCapacity: number | null = React.useMemo(() => {
    if (votingCapacity == null) return null;
    return Math.max(0, Number(votingCapacity) || 0);
  }, [votingCapacity]);

  const maxSelectableAmount = React.useMemo(() => {
    if (remainingVotingCapacity == null) return Math.max(0, fansBalance);
    return Math.max(0, Math.min(fansBalance, remainingVotingCapacity));
  }, [fansBalance, remainingVotingCapacity]);

  const fetchPreviousVoteTxData = async () => {
    try {
      const txResp = await telegramClientApi.getVoteTransaction(0, projectAddress!);

      if (txResp.httpCode === 404) {
        setVoteTxData(emptyVoteTxData);
        return;
      }

      if (txResp?.error?.message) {
        setVoteTxData({
          ...emptyVoteTxData,
          error: txResp.error.message
        });
        return;
      }

      if (txResp?.data) {
        const data = txResp.data as VoteTxData;
        setAmount(data.token_amount ?? 0);

        setVoteTxData({
          ...data,
          error: null
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch vote transaction: ', err);
      setVoteTxData({
        ...emptyVoteTxData,
        error: err.message
      });
    }
  };

  const expirationCountdown = useCountdown(voteTxData?.expiration ?? 0);

  const votingCapacityResetCountdown = useCountdown(
    remainingVotingCapacity != null && remainingVotingCapacity === 0
      ? (voteTxData?.voting_capacity_resets_at ?? 0)
      : 0
  );

  // when the user is going to be sent to CWM, some balance checks are not needed
  const mustBeRedirectedToCWM = shouldBeRedirectedToCWM();

  const isInsufficientCSPRBalance = React.useMemo(
    () => !mustBeRedirectedToCWM && walletBalance < txFee,
    [mustBeRedirectedToCWM, walletBalance, txFee]
  );

  const isInsufficientFANSBalance = React.useMemo(
    () => (fansBalance === 0 || fansBalance < amount) && !expirationCountdown?.active,
    [fansBalance, amount, expirationCountdown]
  );

  const handleAmountChange = React.useCallback(
    (val: string | number) => {
      if (expirationCountdown?.active || isInsufficientCSPRBalance || isInsufficientFANSBalance)
        return;

      const parsed = parseAmount(val);
      setAmount((prev) => {
        const next = clamp(parsed, 0, maxSelectableAmount);
        return Number.isFinite(next) ? next : prev;
      });
    },
    [expirationCountdown, isInsufficientCSPRBalance, isInsufficientFANSBalance, maxSelectableAmount]
  );

  // If user clicks preset that exceeds remaining capacity -> set to remaining
  const handleSelectPresetAmount = React.useCallback(
    (preset: number) => {
      handleAmountChange(Math.min(preset, maxSelectableAmount));
    },
    [handleAmountChange, maxSelectableAmount]
  );

  // MAX = remaining capacity (also limited by FAN balance via maxSelectableAmount)
  const handleSendMax = React.useCallback(() => {
    handleAmountChange(maxSelectableAmount);
  }, [handleAmountChange, maxSelectableAmount]);

  const disabled = React.useMemo(
    () =>
      submitting ||
      amount <= 0 ||
      !senderPK ||
      !projectAddress ||
      isInsufficientCSPRBalance ||
      isInsufficientFANSBalance ||
      maxSelectableAmount <= 0,
    [
      submitting,
      amount,
      senderPK,
      projectAddress,
      isInsufficientCSPRBalance,
      isInsufficientFANSBalance,
      maxSelectableAmount
    ]
  );

  const handleSend = React.useCallback(async () => {
    if (disabled) return;

    if (mustBeRedirectedToCWM) {
      const platform = getDevicePlatform();
      const encodedData = getEncodedAuthData(initData || '');
      const redirectLink = buildRedirectLinkToCWM(platform, encodedData);
      openLink(redirectLink)();
    }

    const mapStatus: Record<string, TxStatus> = {
      ping: 'processing',
      sent: 'processing',
      processed: 'processed'
    };

    const onStatusUpdate = (status: string) => {
      const txModalStatus: TxStatus = mapStatus[status] ?? 'error';

      if (txStatus !== txModalStatus) {
        dispatch(setTxStatus(txModalStatus));
      }

      if (!isTxModalOpen && status !== 'cancelled' && txStatus !== txModalStatus) {
        dispatch(openTxModal({ status: txModalStatus }));
      }

      if (txModalStatus === 'processed') {
        dispatch(setTxStatus(txModalStatus));
        setTimeout(() => {
          onProcessed?.();
          setVoteTxData(null);
          meService.refresh();
        }, 500);
      }
    };

    setSubmitting(true);

    try {
      let payload = voteTxData?.transaction;

      if (!payload) {
        const txResp = await telegramClientApi.getVoteTransaction(amount, projectAddress!);

        if (txResp?.error?.message) {
          setVoteTxData({
            ...emptyVoteTxData,
            error: txResp.error.message
          });
          return;
        }

        const data = txResp.data;
        payload = data?.transaction;

        if (!data) {
          throw new Error('Empty transaction response');
        }
      }

      if (!payload) throw new Error('Empty transaction payload');

      await meService.refresh();

      const csprclick = (window as any).csprclick;
      if (!csprclick?.send) throw new Error('Wallet bridge is unavailable');

      const res = await csprclick.send(JSON.stringify(payload), senderPK!, onStatusUpdate);

      if (res?.cancelled) {
        setVoteTxData({
          ...emptyVoteTxData,
          error: 'Transaction was cancelled. Please try again.'
        });
      } else if (res?.error) {
        const error = String(
          res.error +
          ' data:' +
          JSON.stringify(res.errorData || 'no error data') +
          ' senderPK: ' +
          senderPK || 'Something went wrong. Please try again.'
        );
        setVoteTxData({
          ...emptyVoteTxData,
          error
        });
        dispatch(setTxStatus('error'));
      }
    } catch (err: any) {
      const actacc = (window as any).csprclick?.getActiveAccount();
      const st = actacc ? JSON.stringify(actacc) : 'no active account';
      const error = err?.message
        ? err.message + ' 1senderPK: ' + st
        : 'Something went wrong. Please try again.';
      setVoteTxData({
        ...emptyVoteTxData,
        error
      });
      dispatch(setTxStatus('error'));
    } finally {
      setSubmitting(false);
    }
  }, [
    amount,
    projectAddress,
    senderPK,
    disabled,
    dispatch,
    isTxModalOpen,
    onProcessed,
    txStatus,
    voteTxData,
    initData,
    meService,
    mustBeRedirectedToCWM
  ]);

  const handleTxModalClose = () => {
    dispatch(closeTxModal());
  };

  const sendMax = amount === maxSelectableAmount && maxSelectableAmount > 0;

  return {
    voteAmounts,
    amount,
    sendMax,
    voteTxTokenAmount: voteTxData?.token_amount,
    voteTxError: voteTxData?.error,
    voteTxFee: txFee.toString(),
    expirationCountdown,
    votingCapacity,
    isVotingCapacityKnown,
    remainingVotingCapacity: remainingVotingCapacity ?? 0,
    remainingVotingCapacityNullable: remainingVotingCapacity,
    maxSelectableAmount,
    votingCapacityResetCountdown,
    isTxModalOpen,
    handleTxModalClose,
    txStatus,
    submitting,
    disabled,
    isInsufficientCSPRBalance,
    isInsufficientFANSBalance,
    fetchPreviousVoteTxData,
    handleAmountChange,
    handleSelectPresetAmount,
    handleSendMax,
    handleSend,
    airdropInfo
  };
};
